import isChunkLoadError from './isChunkLoadError.js';

// A failed chunk cannot be retried in place, so reloading is the retry. Bounded
// per window and per tab — sessionStorage survives a reload but not a new tab.
const RETRY_WINDOW_MS = 10 * 60 * 1000;
const MAX_RECOVERY_RELOADS = 3;
const STORAGE_KEY = '__joybird_chunk_reload';

const NO_ATTEMPTS = { count: 0, at: 0, pending: null };

const readAttempts = () => {
  try {
    // JSON.parse(null) returns null rather than throwing.
    const stored = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY));
    if (!stored || typeof stored !== 'object') {
      return NO_ATTEMPTS;
    }
    return {
      count: Number(stored.count) || 0,
      at: Number(stored.at) || 0,
      pending:
        stored.pending && typeof stored.pending === 'object'
          ? stored.pending
          : null,
    };
  } catch {
    return NO_ATTEMPTS;
  }
};

// False when the marker cannot be persisted (private mode). No marker means no
// loop guard, so the caller must skip the reload rather than risk looping.
const markAttempt = attempt => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attempt));
    return true;
  } catch {
    return false;
  }
};

// Reloads once to recover from a chunk-load failure. Returns true when a reload
// started, so the caller can skip reporting a failure that is about to recover.
export const attemptChunkLoadRecovery = (
  error,
  { origin = 'unknown' } = {}
) => {
  if (typeof window === 'undefined' || !isChunkLoadError(error)) {
    return false;
  }

  // navigator.onLine is unreliable when true but trustworthy when false, and a
  // reload while offline cannot fetch the chunk — it would only cost a request.
  if (window.navigator?.onLine === false) {
    return false;
  }

  const { count, at } = readAttempts();
  if (count >= MAX_RECOVERY_RELOADS) {
    return false;
  }

  // A backwards clock makes this negative, which also reads as "too soon".
  const now = Date.now();
  if (now - at < RETRY_WINDOW_MS) {
    return false;
  }

  const attempt = count + 1;
  // Reporting here would not survive the reload aborting the transport send, so
  // record it for the next document load — which only counts reloads that ran.
  if (!markAttempt({ count: attempt, at: now, pending: { origin, attempt } })) {
    return false;
  }

  window.location.reload();
  return true;
};

// Reads and clears the record left by a recovery reload. Called once per document
// load so recovered failures stay countable rather than silently disappearing.
export const takePendingRecoveryReport = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const { count, at, pending } = readAttempts();
  if (!pending) {
    return null;
  }

  // Clear the report flag, keep the loop-guard budget.
  if (!markAttempt({ count, at, pending: null })) {
    return null;
  }
  return pending;
};

export default attemptChunkLoadRecovery;
